import type { StyleProperty } from '@xshop/shared';

// What a bare number means, property by property.
//
// This table is the likeliest silent bug in the whole engine, so it is written out in full rather than
// guessed at from the property's name: `fontSize: 16` is sixteen pixels, `lineHeight: 1.5` is a ratio and
// a `px` on it would set the line to a pixel and a half, `opacity: 0.5` and `zIndex: 3` carry no unit at
// all, and a number on a keyword property such as `color` means nothing and is written nowhere.
//
// `satisfies Record<StyleProperty, StyleUnit>` is the gate: a property added to the closed
// `STYLE_PROPERTIES` list fails to compile here until someone has said what a number on it means.

/** How a bare number is read for one property: as pixels, as itself, as milliseconds, or not at all. */
export type StyleUnit = 'px' | 'none' | 'duration' | 'keyword';

export const STYLE_UNITS = {
  // Layout — the box, and how it lines its children up.
  display: 'keyword', flexDirection: 'keyword', flexWrap: 'keyword', justifyContent: 'keyword',
  alignItems: 'keyword', alignSelf: 'keyword', gap: 'px', columnGap: 'px', rowGap: 'px',
  gridColumns: 'none', order: 'none', flexGrow: 'none', flexShrink: 'none',
  // Space around it and inside it.
  marginTop: 'px',
  marginInlineStart: 'px',
  marginInlineEnd: 'px',
  paddingInlineStart: 'px',
  paddingInlineEnd: 'px', marginRight: 'px', marginBottom: 'px', marginLeft: 'px',
  paddingTop: 'px', paddingRight: 'px', paddingBottom: 'px', paddingLeft: 'px',
  // Size. An aspect ratio is a ratio, not a length.
  width: 'px', minWidth: 'px', maxWidth: 'px', height: 'px', minHeight: 'px', maxHeight: 'px',
  aspectRatio: 'none',
  // Type. Weight and line height are the two ratios a pixel would quietly ruin.
  color: 'keyword', fontFamily: 'keyword', fontSize: 'px', fontWeight: 'none', fontStyle: 'keyword',
  lineHeight: 'none', letterSpacing: 'px', textAlign: 'keyword', textTransform: 'keyword',
  textDecoration: 'keyword', whiteSpace: 'keyword', textOverflow: 'keyword',
  // Background.
  backgroundColor: 'keyword', backgroundImage: 'keyword', backgroundSize: 'px',
  backgroundPosition: 'px', backgroundRepeat: 'keyword',
  // Border and the four corners.
  borderStyle: 'keyword', borderWidth: 'px', borderColor: 'keyword',
  borderTopWidth: 'px', borderRightWidth: 'px', borderBottomWidth: 'px', borderLeftWidth: 'px',
  borderTopLeftRadius: 'px', borderTopRightRadius: 'px',
  borderBottomRightRadius: 'px', borderBottomLeftRadius: 'px',
  // Paint.
  boxShadow: 'keyword', textShadow: 'keyword', opacity: 'none', overflow: 'keyword',
  objectFit: 'keyword', cursor: 'keyword',
  // Position.
  position: 'keyword', top: 'px', right: 'px', bottom: 'px', left: 'px', zIndex: 'none',
  // Motion. A number on a transition is the duration the inspector's slider carries.
  transition: 'duration', transform: 'keyword', animation: 'keyword'
} satisfies Record<StyleProperty, StyleUnit>;

/** The one stored property whose CSS name is not simply its kebab-case twin. */
const CSS_PROPERTY_OVERRIDES: Partial<Record<StyleProperty, string>> = { gridColumns: 'grid-template-columns' };

// Answers the CSS property one stored name writes, which is its kebab-case twin but for the grid's columns.
export function cssPropertyName(property: StyleProperty): string {
  return CSS_PROPERTY_OVERRIDES[property] ?? property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

// Rounds a stored number to the three decimals a stylesheet can use, so one value always writes one text.
function trimNumber(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

// Reads a bare number through its property's unit: pixels, itself, a duration, or nothing at all where a
// number means nothing, which is what keeps `color: 5` out of a shopper's stylesheet.
export function formatStyleNumber(property: StyleProperty, value: number): string | null {
  if (!Number.isFinite(value)) return null;
  const unit = STYLE_UNITS[property];
  if (unit === 'px') return `${trimNumber(value)}px`;
  if (unit === 'none') return trimNumber(value);
  return unit === 'duration' ? `all ${trimNumber(value)}ms` : null;
}
